/**
 * Firefox-specific video validation utilities.
 * Firefox has inconsistent behavior with video validation events:
 * - onloadedmetadata may not fire reliably
 * - onloadeddata may fire before metadata is ready
 * - oncanplay may not fire at all
 */

interface VideoValidationOptions {
	onLoadedMetadata?: () => void;
	onLoadedData?: () => void;
	onCanPlay?: () => void;
	onError?: (error: Error) => void;
	onTimeout?: () => void;
}

const addVideoValidationListeners = (video: HTMLVideoElement, options: VideoValidationOptions): { cleanup: () => void } => {
	const timeout = setTimeout(() => {
		options.onTimeout?.();
	}, 1000);

	const handleLoadedData = () => {
		options.onLoadedData?.();
	};

	const handleCanPlay = () => {
		options.onCanPlay?.();
	};

	const handleLoadedMetadata = () => {
		options.onLoadedMetadata?.();
	};

	const handleError = () => {
		options.onError?.(new Error('Invalid video data - failed to parse'));
	};

	// Add all event listeners
	video.addEventListener('loadeddata', handleLoadedData);
	video.addEventListener('canplay', handleCanPlay);
	video.addEventListener('loadedmetadata', handleLoadedMetadata);
	video.addEventListener('error', handleError);

	return {
		cleanup: () => {
			clearTimeout(timeout);
			video.removeEventListener('loadeddata', handleLoadedData);
			video.removeEventListener('canplay', handleCanPlay);
			video.removeEventListener('loadedmetadata', handleLoadedMetadata);
			video.removeEventListener('error', handleError);
		},
	};
};

export const validateVideo = (blob: Blob): Promise<void> => {
	return new Promise((resolve, reject) => {
		const video = document.createElement('video');
		const url = URL.createObjectURL(blob);

		const cleanup = () => {
			URL.revokeObjectURL(url);
			video.remove();
		};

		const validation = addVideoValidationListeners(video, {
			onLoadedMetadata: () => {
				validation.cleanup();
				cleanup();
				resolve();
			},
			onLoadedData: () => {
				validation.cleanup();
				cleanup();
				resolve();
			},
			onCanPlay: () => {
				validation.cleanup();
				cleanup();
				resolve();
			},
			onError: (error) => {
				validation.cleanup();
				cleanup();
				reject(error);
			},
			onTimeout: () => {
				validation.cleanup();
				cleanup();
				resolve();
			},
		});

		video.src = url;
	});
};
